import React from 'react';
import { MapPin, Users, Euro, UserCheck, Edit3, Trash2, Clock, Landmark } from 'lucide-react';
import { ConstraintViolation, EventItem } from '../../core/types.ts';
import { calculateEventFinancials } from '../../core/financials.ts';
import { LintBadge } from './LintBadge.tsx';

interface EventCardProps {
  event: EventItem;
  violations: ConstraintViolation[];
  onEdit: (event: EventItem) => void;
  onDelete: (id: string) => void;
  onDragStart: (e: React.DragEvent, eventId: string) => void;
  isCollapsed?: boolean;
}

const CATEGORY_COLORS: Record<string, string> = {
  Culture: 'var(--cat-culture)',
  Drinking: 'var(--cat-drinking)',
  Sports: 'var(--cat-sports)',
  Social: 'var(--cat-social)',
  Academic: 'var(--cat-academic)',
  Trip: 'var(--cat-trip)',
  Art: 'var(--cat-art)',
  Other: 'var(--cat-other)',
};

const FUNDING_LABELS: Record<string, string> = {
  'stibet-free': 'STIBET Free',
  'stibet-partial': 'STIBET Grant',
  'fully-free': 'Free',
  'external': 'External',
  'self-funded': 'Self-Funded',
};

export const EventCard: React.FC<EventCardProps> = ({
  event,
  violations,
  onEdit,
  onDelete,
  onDragStart,
  isCollapsed = false,
}) => {
  const catColor = CATEGORY_COLORS[event.category] || CATEGORY_COLORS.Other;
  const financialDetails = calculateEventFinancials(event);

  const formatTime = (iso: string) => {
    try {
      const d = new Date(iso);
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
    } catch (_e) {
      return iso;
    }
  };

  if (isCollapsed) {
    return (
      <div
        className="event-card event-card-collapsed"
        draggable
        onDragStart={e => onDragStart(e, event.id)}
        style={{
          background: `linear-gradient(135deg, color-mix(in srgb, ${catColor} 18%, transparent) 0%, color-mix(in srgb, ${catColor} 5%, transparent) 100%), var(--bg-elevated)`,
          borderColor: `color-mix(in srgb, ${catColor} 45%, var(--border))`,
          boxShadow: `0 4px 12px -4px color-mix(in srgb, ${catColor} 15%, transparent)`,
        }}
      >
        <div className="event-card-header">
          <span
            className="event-title event-title-clickable"
            onClick={e => {
              e.stopPropagation();
              onEdit(event);
            }}
            title="Click to view details / edit"
            style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
          >
            {event.title}
          </span>
        </div>

        <div className="event-time" style={{ color: 'var(--text-primary)' }}>
          <Clock size={14} strokeWidth={2} style={{ color: catColor }} />
          <span>{formatTime(event.start)} – {formatTime(event.end)}</span>
        </div>
      </div>
    );
  }

  const modelLabel = FUNDING_LABELS[financialDetails.fundingModel] || financialDetails.fundingModel;

  return (
    <div
      className="event-card"
      draggable
      onDragStart={e => onDragStart(e, event.id)}
      style={{
        background: `linear-gradient(135deg, color-mix(in srgb, ${catColor} 18%, transparent) 0%, color-mix(in srgb, ${catColor} 5%, transparent) 100%), var(--bg-elevated)`,
        borderColor: `color-mix(in srgb, ${catColor} 45%, var(--border))`,
        boxShadow: `0 8px 24px -6px color-mix(in srgb, ${catColor} 20%, transparent)`,
      }}
    >
      <div className="event-card-header">
        <span
          className="event-title event-title-clickable"
          onClick={e => {
            e.stopPropagation();
            onEdit(event);
          }}
          title="Click to view details / edit"
          style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
        >
          {event.title}
        </span>
        <div className="event-actions">
          <button
            className="icon-btn"
            onClick={e => {
              e.stopPropagation();
              onEdit(event);
            }}
            title="Edit Event"
          >
            <Edit3 size={16} strokeWidth={1.8} />
          </button>
          <button
            className="icon-btn danger"
            onClick={e => {
              e.stopPropagation();
              onDelete(event.id);
            }}
            title="Delete Event"
          >
            <Trash2 size={16} strokeWidth={1.8} />
          </button>
        </div>
      </div>

      <div className="event-time" style={{ color: 'var(--text-primary)' }}>
        <Clock size={15} strokeWidth={2} style={{ color: catColor }} />
        <span>{formatTime(event.start)} – {formatTime(event.end)}</span>
      </div>

      <div className="event-meta-grid" style={{ borderColor: `color-mix(in srgb, ${catColor} 30%, var(--border-subtle))` }}>
        <div className="meta-item" title={`Location: ${event.location}`}>
          <MapPin size={14} strokeWidth={1.8} style={{ color: catColor }} />
          <span>{event.location}</span>
        </div>
        <div className="meta-item" title={`Capacity: ${event.capacity}`}>
          <Users size={14} strokeWidth={1.8} style={{ color: catColor }} />
          <span>{event.capacity} seats</span>
        </div>
        <div className="meta-item" title={`Ticket: €${financialDetails.actualPricePerParticipant} / STIBET: €${financialDetails.appliedStibetFunding.toFixed(0)}`}>
          <Euro size={14} strokeWidth={1.8} style={{ color: catColor }} />
          <span>
            {financialDetails.actualPricePerParticipant === 0 ? 'FREE' : `€${financialDetails.actualPricePerParticipant}`}
            {financialDetails.appliedStibetFunding > 0 ? ` (STIBET €${financialDetails.appliedStibetFunding.toFixed(0)})` : ''}
          </span>
        </div>
        <div className="meta-item" title={`Organisers: ${event.organisers}`}>
          <UserCheck size={14} strokeWidth={1.8} style={{ color: catColor }} />
          <span>{event.organisers} orgs</span>
        </div>
      </div>

      <div className="event-badges">
        <span
          className="cat-badge"
          style={{
            color: 'var(--text-primary)',
            background: `color-mix(in srgb, ${catColor} 22%, transparent)`,
            borderColor: `color-mix(in srgb, ${catColor} 50%, transparent)`,
          }}
        >
          {event.category}
        </span>

        <span className={`brand-badge ${financialDetails.appliedStibetFunding > 0 ? 'brand-badge-emerald' : 'brand-badge-muted'}`}>
          {modelLabel}
        </span>

        <LintBadge violations={violations} />
      </div>
    </div>
  );
};

